import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Book, Upload, Search, FileText, Trash2, Folder, FolderPlus,
  ChevronRight, Home as HomeIcon, Loader2, Pencil, Download,
} from 'lucide-react';
import { apiClient, ApiError } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

interface KBFolder {
  id: string;
  name: string;
  parentId: string | null;
  createdBy: string | null;
  createdByName: string;
  createdAt: string;
}

interface KBDocument {
  id: string;
  name: string;
  originalFilename: string;
  folderId: string | null;
  contentType: string;
  fileSize: number;
  status: 'uploaded' | 'processing' | 'completed' | 'error';
  processedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName: string;
}

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const StatusBadge: React.FC<{ status: KBDocument['status'] }> = ({ status }) => {
  const map: Record<KBDocument['status'], { label: string; className: string }> = {
    uploaded: { label: 'Queued', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    processing: { label: 'Processing', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    completed: { label: 'Ready', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
    error: { label: 'Failed', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  };
  const { label, className } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${className}`}>
      {status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
      {label}
    </span>
  );
};

const KnowledgeBase: React.FC = () => {
  const { user } = useAuth();
  const [folders, setFolders] = useState<KBFolder[]>([]);
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'system_admin';
  const canManage = useCallback(
    (createdBy: string | null) => createdBy === user?.id || isAdmin,
    [user?.id, isAdmin]
  );

  const loadFolders = useCallback(async () => {
    try {
      const data = await apiClient.get<KBFolder[]>('/api/agency-knowledge/folders');
      setFolders(data || []);
    } catch (e) {
      console.error('Failed to load folders:', e);
    }
  }, []);

  const loadDocuments = useCallback(async (folderId: string | null) => {
    try {
      const param = folderId ? `?folderId=${folderId}` : '?folderId=root';
      const data = await apiClient.get<KBDocument[]>(`/api/agency-knowledge/documents${param}`);
      setDocuments(data || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadFolders(), loadDocuments(currentFolderId)]);
      setLoading(false);
    })();
  }, [currentFolderId, loadFolders, loadDocuments]);

  // Poll while any document is still processing so the status badge updates.
  useEffect(() => {
    if (!documents.some((d) => d.status === 'processing' || d.status === 'uploaded')) return;
    const timer = setInterval(() => loadDocuments(currentFolderId), 5000);
    return () => clearInterval(timer);
  }, [documents, currentFolderId, loadDocuments]);

  const childFolders = useMemo(
    () => folders.filter((f) => f.parentId === currentFolderId),
    [folders, currentFolderId]
  );

  // Breadcrumb path from root to the current folder.
  const breadcrumb = useMemo(() => {
    const path: KBFolder[] = [];
    let cursor = currentFolderId;
    const byId = new Map(folders.map((f) => [f.id, f]));
    while (cursor) {
      const f = byId.get(cursor);
      if (!f) break;
      path.unshift(f);
      cursor = f.parentId;
    }
    return path;
  }, [folders, currentFolderId]);

  const filteredDocuments = useMemo(
    () =>
      documents.filter(
        (d) =>
          d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.originalFilename.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [documents, searchQuery]
  );

  const handleCreateFolder = async () => {
    const name = window.prompt('New folder name:');
    if (!name || !name.trim()) return;
    try {
      await apiClient.post('/api/agency-knowledge/folders', { name: name.trim(), parentId: currentFolderId });
      await loadFolders();
    } catch (e) {
      alert(`Failed to create folder: ${(e as ApiError).message || 'Unknown error'}`);
    }
  };

  const handleRenameFolder = async (folder: KBFolder) => {
    const name = window.prompt('Rename folder:', folder.name);
    if (!name || !name.trim() || name.trim() === folder.name) return;
    try {
      await apiClient.patch(`/api/agency-knowledge/folders/${folder.id}`, { name: name.trim() });
      await loadFolders();
    } catch (e) {
      alert(`Failed to rename folder: ${(e as ApiError).message || 'Unknown error'}`);
    }
  };

  const handleDeleteFolder = async (folder: KBFolder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and everything inside it? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/agency-knowledge/folders/${folder.id}`);
      await loadFolders();
      if (currentFolderId === folder.id) setCurrentFolderId(folder.parentId);
    } catch (e) {
      alert(`Failed to delete folder: ${(e as ApiError).message || 'Unknown error'}`);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (currentFolderId) formData.append('folderId', currentFolderId);
      const newDoc = await apiClient.post<KBDocument>('/api/agency-knowledge/documents', formData);
      setDocuments((prev) => [newDoc, ...prev]);
    } catch (e) {
      alert(`Upload failed: ${(e as ApiError).message || 'Unknown error'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (doc: KBDocument) => {
    if (!window.confirm(`Delete "${doc.name}" from the knowledge base?`)) return;
    try {
      await apiClient.delete(`/api/agency-knowledge/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e) {
      alert(`Failed to delete: ${(e as ApiError).message || 'Unknown error'}`);
    }
  };

  const handleDownload = async (doc: KBDocument) => {
    try {
      const { url } = await apiClient.get<{ url: string }>(`/api/agency-knowledge/documents/${doc.id}/download`);
      window.open(url, '_blank');
    } catch (e) {
      alert(`Failed to open document: ${(e as ApiError).message || 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-secondary dark:text-dark-text">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-50 dark:bg-dark-bg p-6 overflow-hidden">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Book className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-secondary dark:text-dark-text">Knowledge Base</h1>
        </div>
        <p className="text-secondary dark:text-dark-text opacity-75">
          Company-wide documents and SOPs. Anything here grounds Arie when you switch the chat bubble to
          <strong> Agency Knowledge</strong>.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={handleCreateFolder}
          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
        >
          <FolderPlus className="w-4 h-4" />
          New Folder
        </button>
        <label className="flex items-center gap-2 px-3 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg cursor-pointer transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload Document'}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.msg,.eml"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent dark:text-dark-muted" />
          <input
            type="text"
            placeholder="Search documents in this folder..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-secondary dark:text-dark-text focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center flex-wrap gap-1 mb-4 text-sm">
        <button
          onClick={() => setCurrentFolderId(null)}
          className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-surface ${
            currentFolderId === null ? 'text-primary font-medium' : 'text-secondary dark:text-dark-text'
          }`}
        >
          <HomeIcon className="w-4 h-4" /> Knowledge Base
        </button>
        {breadcrumb.map((f) => (
          <React.Fragment key={f.id}>
            <ChevronRight className="w-4 h-4 text-accent dark:text-dark-muted" />
            <button
              onClick={() => setCurrentFolderId(f.id)}
              className={`px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-surface ${
                currentFolderId === f.id ? 'text-primary font-medium' : 'text-secondary dark:text-dark-text'
              }`}
            >
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border flex-1 overflow-auto">
        {/* Folders */}
        {childFolders.length > 0 && (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-b border-gray-100 dark:border-dark-border">
            {childFolders.map((folder) => (
              <div
                key={folder.id}
                className="group flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-dark-border hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                onClick={() => setCurrentFolderId(folder.id)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Folder className="w-5 h-5 text-primary shrink-0" />
                  <span className="font-medium text-secondary dark:text-dark-text truncate">{folder.name}</span>
                </div>
                {canManage(folder.createdBy) && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRenameFolder(folder); }}
                      className="p-1 text-accent dark:text-dark-muted hover:text-primary"
                      title="Rename folder"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                      className="p-1 text-accent dark:text-dark-muted hover:text-red-600"
                      title="Delete folder"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Documents */}
        {filteredDocuments.length === 0 && childFolders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Book className="w-12 h-12 text-accent dark:text-dark-muted mb-4" />
            <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
              {documents.length === 0 ? 'This folder is empty' : 'No documents match your search'}
            </h3>
            <p className="text-accent dark:text-dark-muted">
              {documents.length === 0 ? 'Upload a document or create a folder to get started' : 'Try adjusting your search'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="group flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-accent dark:text-dark-muted shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-secondary dark:text-dark-text truncate">{doc.name}</h4>
                      <StatusBadge status={doc.status} />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-accent dark:text-dark-muted mt-1">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>•</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>By {doc.createdByName}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-accent dark:text-dark-muted hover:text-primary"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  {canManage(doc.createdBy) && (
                    <button
                      onClick={() => handleDeleteDocument(doc)}
                      className="p-2 text-accent dark:text-dark-muted hover:text-red-600"
                      title="Delete document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
